/**
 * What a stored bolt circle MEANS, in millimetres — and which Console option
 * stands for it (P25009-488).
 *
 * One bolt circle reaches a listing in several spellings, and each of these is
 * on a real listing today:
 *
 *   "114_3"  Console's option key. The wizard writes it, and since P25009-488
 *            every import path does too.
 *   "1397"   Console's key for 139.7, and "1651" for 165.1: the decimal point
 *            was lost when those options were made.
 *   "114.3"  what the imports stored before P25009-488 (and "139.7").
 *   "114,3"  the Swedish decimal comma (Gummigrossen, CSV files).
 *   "114"    the trade's short form of 114.3 ("5/114"). No wheel uses a
 *            114.0 mm circle, and Console has no "114" option.
 *   "1143"   114.3 with its point lost, the way "1397" is 139.7.
 *
 * A search filter compares the stored text with Console's key character for
 * character, so a listing must STORE the key — "114.3" is a listing the
 * 5x114,3 filter never finds. Everything that needs the NUMBER (the car
 * search, the package engine, titles, the listing page) reads it through
 * `boltCircleMm`, so all of these spellings are one value to it.
 *
 * Written as CommonJS on purpose, like fitmentFactsCore.js: shared by the
 * browser bundle and the plain-Node API server, which is not transpiled.
 */

/* Real bolt circles are 98–205 mm. A four-digit whole number is a
 * three-digit circle whose decimal point was lost: 1143, 1397, 1651. */
const LOST_POINT_MIN = 1000;
const LOST_POINT_MAX = 9999;

/* The trade's short forms, by the number written. */
const SHORTHAND_MM = { 114: 114.3 };

/**
 * The bolt circle in mm, or null when the value is not a bolt circle at all
 * ("other", "", "5x114.3" — a pattern is not a circle).
 *
 *   "114_3", "114.3", "114,3", "114", "1143", 114.3 -> 114.3
 *   "1397", "139.7", "139,7", "139_7"              -> 139.7
 *   "112", 112                                      -> 112
 */
const boltCircleMm = value => {
  if (value === null || value === undefined) return null;
  const text = String(value)
    .trim()
    .replace(/[,_]/, '.');
  if (!/^\d+(\.\d+)?$/.test(text)) return null;
  const mm = Number(text);
  if (!Number.isFinite(mm) || mm <= 0) return null;
  if (Number.isInteger(mm) && mm >= LOST_POINT_MIN && mm <= LOST_POINT_MAX) return mm / 10;
  return SHORTHAND_MM[mm] || mm;
};

/** The circle as a plain decimal-point number, "114.3" or "108"; '' when unreadable. */
const boltCircleText = value => {
  const mm = boltCircleMm(value);
  return mm === null ? '' : String(mm);
};

/**
 * The Console option key for a bolt circle, or null when no option means it.
 *
 * `options` are Console's boltCircle options, `{ option, label }` — the live
 * config's (server) or the hosted listing-fields asset's (browser). An option
 * matches by its key ("114_3") or by its label ("139.7" is the only place the
 * key `1397` can be read as a number, though boltCircleMm reads it too). Two
 * options meaning the same circle are no match: picking one would be a guess.
 */
const boltCircleOptionKey = (value, options) => {
  const mm = boltCircleMm(value);
  if (mm === null || !Array.isArray(options)) return null;
  const keys = [
    ...new Set(
      options
        .filter(
          entry =>
            entry &&
            (boltCircleMm(entry.option) === mm || boltCircleMm(entry.label) === mm)
        )
        .map(entry => String(entry.option))
    ),
  ];
  return keys.length === 1 ? keys[0] : null;
};

/**
 * Every spelling a listing may have stored for this circle, for a search
 * filter that must find listings written before and after P25009-488:
 * 139.7 -> ["139.7", "139_7", "1397"]. A whole number has one spelling.
 */
const boltCircleSpellings = value => {
  const mm = boltCircleMm(value);
  if (mm === null) return [];
  const plain = String(mm);
  if (Number.isInteger(mm)) return [plain];
  return [...new Set([plain, plain.replace('.', '_'), plain.replace('.', '')])];
};

module.exports = {
  boltCircleMm,
  boltCircleText,
  boltCircleOptionKey,
  boltCircleSpellings,
};
